import {
  EsignFieldKind,
  EsignFinalPackageStatus,
  EsignRequestStatus,
  EsignSignerStatus,
  PublicSignerSubmitSuccessResponseCompletionStateEnum
} from '@/types/esign';

import type {
  PublicSignerFieldResponse,
  PublicSignerSessionResponse,
  PublicSignerSubmitResponse
} from './publicSignerTypes';

function isRecord(payload: unknown): payload is Record<string, unknown> {
  return (
    Boolean(payload) && typeof payload === 'object' && !Array.isArray(payload)
  );
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isEnumValue<T extends Record<string, string>>(
  enumValues: T,
  value: unknown
): value is T[keyof T] {
  return (
    typeof value === 'string' &&
    Object.values(enumValues).includes(value as T[keyof T])
  );
}

// Drawn signatures render via <img src>. A non-data URL would fire a real
// network request and leak Referer: /sign/:token (the bearer token) to a
// third-party origin.
const DRAWN_SIGNATURE_DATA_URL_PREFIX = 'data:image/png;base64,';
const MAX_DRAWN_SIGNATURE_BYTES = 1_000_000;

function isAcceptableDrawnImageDataUrl(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    value.startsWith(DRAWN_SIGNATURE_DATA_URL_PREFIX) &&
    value.length <= MAX_DRAWN_SIGNATURE_BYTES
  );
}

function isSignatureValue(value: unknown): boolean {
  if (!isRecord(value) || typeof value.type !== 'string') {
    return false;
  }

  if (value.type === 'typed') {
    return typeof value.typed_name === 'string';
  }

  if (value.type === 'drawn') {
    return isAcceptableDrawnImageDataUrl(value.drawn_image_data_url);
  }

  return false;
}

function isFieldResponseValue(
  fieldKind: unknown,
  value: unknown
): value is PublicSignerFieldResponse['value'] {
  if (fieldKind === EsignFieldKind.Text) {
    return value === null || typeof value === 'string';
  }

  if (fieldKind === EsignFieldKind.Checkbox) {
    return value === null || typeof value === 'boolean';
  }

  if (fieldKind === EsignFieldKind.Signature) {
    return value === null || isSignatureValue(value);
  }

  if (fieldKind === EsignFieldKind.RadioGroup) {
    return value === null || typeof value === 'string';
  }

  return false;
}

function isRadioOption(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.label === 'string' &&
    typeof value.page_index === 'number' &&
    typeof value.x === 'number' &&
    typeof value.y === 'number' &&
    typeof value.width === 'number' &&
    typeof value.height === 'number' &&
    typeof value.sort_order === 'number'
  );
}

function hasValidRadioSelection(value: unknown, options: unknown[]): boolean {
  return (
    value === null ||
    options.some((option) => isRecord(option) && option.id === value)
  );
}

function isNullableTimestamp(value: unknown): value is string | null {
  return value === null || typeof value === 'string' || value instanceof Date;
}

export function assertPublicSignerFieldResponse(
  payload: unknown
): PublicSignerFieldResponse {
  if (
    isRecord(payload) &&
    typeof payload.id === 'string' &&
    typeof payload.label === 'string' &&
    isNullableString(payload.prefill_source) &&
    isEnumValue(EsignFieldKind, payload.field_kind) &&
    typeof payload.page_index === 'number' &&
    typeof payload.x === 'number' &&
    typeof payload.y === 'number' &&
    typeof payload.width === 'number' &&
    typeof payload.height === 'number' &&
    isFieldResponseValue(payload.field_kind, payload.value) &&
    typeof payload.is_required === 'boolean' &&
    typeof payload.is_sensitive === 'boolean' &&
    typeof payload.is_editable === 'boolean' &&
    (payload.field_kind !== EsignFieldKind.RadioGroup ||
      (Array.isArray(payload.options) &&
        payload.options.length >= 2 &&
        payload.options.every(isRadioOption) &&
        hasValidRadioSelection(payload.value, payload.options)))
  ) {
    return payload as unknown as PublicSignerFieldResponse;
  }

  throw new Error('Received an invalid public signer field response.');
}

export function assertPublicSignerSessionResponse(
  payload: unknown
): PublicSignerSessionResponse {
  if (
    !isRecord(payload) ||
    typeof payload.request_id !== 'string' ||
    !isNullableString(payload.request_title) ||
    !isNullableString(payload.facility_name) ||
    typeof payload.signer_id !== 'string' ||
    !isNullableString(payload.signer_name) ||
    !isNullableString(payload.signer_role) ||
    !isEnumValue(EsignSignerStatus, payload.signer_status) ||
    !isNullableTimestamp(payload.session_expires_at) ||
    !isNullableString(payload.document_url) ||
    !Array.isArray(payload.fields)
  ) {
    throw new Error('Received an invalid public signer session response.');
  }

  const validFields: PublicSignerFieldResponse[] = [];
  for (const field of payload.fields) {
    try {
      validFields.push(assertPublicSignerFieldResponse(field));
    } catch (error) {
      console.warn('Dropping invalid public signer field payload', {
        error,
        fieldId:
          isRecord(field) && typeof field.id === 'string' ? field.id : undefined
      });
    }
  }

  return {
    ...payload,
    fields: validFields
  } as PublicSignerSessionResponse;
}

export function assertPublicSignerSubmitResponse(
  payload: unknown
): PublicSignerSubmitResponse {
  if (
    isRecord(payload) &&
    typeof payload.request_id === 'string' &&
    isNullableString(payload.request_title) &&
    typeof payload.signer_id === 'string' &&
    isEnumValue(EsignSignerStatus, payload.signer_status) &&
    isNullableTimestamp(payload.signer_completed_at) &&
    (payload.request_status === EsignRequestStatus.Pending ||
      payload.request_status === EsignRequestStatus.Completed) &&
    isNullableTimestamp(payload.request_completed_at) &&
    (payload.final_package_status === null ||
      isEnumValue(EsignFinalPackageStatus, payload.final_package_status)) &&
    isEnumValue(
      PublicSignerSubmitSuccessResponseCompletionStateEnum,
      payload.completion_state
    )
  ) {
    return payload as unknown as PublicSignerSubmitResponse;
  }

  throw new Error('Received an invalid public signer submission response.');
}
